import type { AtcVersion, ProposedCard, TabState } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { AtcVersionForm } from './AtcVersionForm.js';
import { AtcVersionHistory } from './AtcVersionHistory.js';

/**
 * 待建卡（proposed）的八个页签。
 *
 * **每个页签都得说清缺什么、由谁补**（P-13：未定义 ≠ 无）。
 * 一片空白说不清是「这里本来就没内容」还是「没加载出来」，更说不清谁该来补；
 * 于是待建卡看起来像一张坏掉的卡，而它其实是一张**正常的、尚未开发的**卡。
 *
 * 「缺什么」不写死在前端：它来自这张卡自己的建设待办，待办由规则表生成。
 * 前端写死的话，改一条规则就会让页面与待办列表各说各话，而两边不一致时没有地方报错。
 *
 * 这一页只有读，没有任何操作控件——待建卡上没有可操作的东西，
 * 它在等的是建设动作，不是在等谁在界面上点一下（B-1、ADR-0004）。
 */

/** 缺项。虚线左边框：**在册、但还没有内容**，与实心的已有内容一眼可分。 */
function Gap({ gap }: { gap: TabState['gaps'][number] }): ReactNode {
  return (
    <li className="pending-gap">
      <div className="pending-gap-head">
        <span className="pending-gap-title">{gap.title}</span>
        <span className="chip wait">卡住 {gap.blocks}</span>
      </div>
      <p className="sub-hint">{gap.detail}</p>
      <p className="sub-hint">
        由谁补：
        {gap.builder === null ? (
          <em>尚未指派建设 ATC——不是没人管，是还没指派</em>
        ) : (
          <code>{gap.builder}</code>
        )}
      </p>
    </li>
  );
}

function TabBody({ tab }: { tab: TabState }): ReactNode {
  return (
    <div className="atc-panel sec-group">
      <section>
        <p className="sub-hint">{tab.note}</p>
      </section>

      {tab.present.length > 0 && (
        <section>
          <h2 className="sec">已有</h2>
          <div className="table-wrap">
            <table>
              <tbody>
                {tab.present.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h2 className="sec">待补全（{tab.gaps.length}）</h2>
        {tab.gaps.length === 0 ? (
          /*
           * 没有待办也要说话。「这一页没有待办」与「这一页没加载出来」
           * 在界面上必须分得开——后者是故障，前者不是。
           */
          <p className="sub-hint">
            这一页没有挂着的建设待办。若你认为它该有，去查待办规则表
            <code>build_todo_rule</code>——页签与待办由同一份规则驱动，
            这里为空说明规则里没有落到本页的条目。
          </p>
        ) : (
          <ul className="pending-gaps">
            {tab.gaps.map((gap) => (
              <Gap key={gap.title} gap={gap} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function ProposedCardDetail({ code }: { code: string }): ReactNode {
  const [card, setCard] = useState<ProposedCard | null>(null);
  const [versions, setVersions] = useState<AtcVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<string>('card');
  const [reloadKey, setReloadKey] = useState(0);

  /*
   * 不在这里 setCard(null) 重置：外层用 `key={code}` 挂载，换一张卡就是换一个实例，
   * 状态本来就是新的。在 effect 里再清一次不但多余，还会让「读取中」多闪一帧。
   */
  useEffect(() => {
    let alive = true;
    api
      .poolCard(code)
      .then((value) => {
        if (alive) setCard(value);
      })
      .catch((cause: unknown) => {
        if (alive) setError(messageOf(cause));
      });
    api
      .atcVersions(code)
      .then((value) => {
        if (alive) setVersions(value.versions);
      })
      .catch(() => {
        // 版本读不出来不该挡住整页——但也不能假装没有，下面会写明。
        if (alive) setVersions([]);
      });
    return () => {
      alive = false;
    };
  }, [code, reloadKey]);

  if (error !== null) return <p className="msg">{error}</p>;
  if (card === null) return <p className="sub-hint">读取中……</p>;

  const tab = card.tabs.find((x) => x.key === active) ?? card.tabs[0];

  return (
    <article className="atc">
      <header className="atc-head">
        <div className="atc-title-row">
          <h1>{card.name}</h1>
          <div className="head-actions">
            <span className="chip wait">待建</span>
            {card.hotlGate && <span className="chip wait">须人工审批</span>}
          </div>
        </div>

        <div className="meta-row">
          <dl className="meta">
            <dt>编码</dt>
            {/* 编码入池即发放、永不改（ADR-0021 二）——客户看到的就是这一个。 */}
            <dd className="mono">{card.code}</dd>
          </dl>
          <dl className="meta">
            <dt>责任岗位</dt>
            <dd>
              {card.departmentName} / {card.positionName}
            </dd>
          </dl>
          <dl className="meta">
            <dt>成熟度</dt>
            <dd>{card.determinism === 'exploratory' ? '探索性' : '确定性'}</dd>
          </dl>
          <dl className="meta">
            <dt>建设待办</dt>
            <dd>
              {card.todoPending} / {card.todoTotal} 未完成
            </dd>
          </dl>
        </div>

        {card.proposalSummary !== null && <p className="sub-hint">{card.proposalSummary}</p>}
      </header>

      <div className="tabline scroll" role="tablist" aria-label="待建卡分区">
        {card.tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={item.key === (tab?.key ?? '')}
            onClick={() => setActive(item.key)}
          >
            {item.label}
            {/* 数字直接标在页签上：不点进去也知道哪一页还欠着东西。 */}
            {item.gaps.length > 0 && <span className="tab-count">{item.gaps.length}</span>}
          </button>
        ))}
      </div>

      {tab !== undefined && tab.key !== 'definition' && <TabBody tab={tab} />}

      {tab !== undefined && tab.key === 'definition' && (
        <div className="atc-panel sec-group">
          <TabBody tab={tab} />
          <section>
            <h2 className="sec">填写这一版</h2>
            <AtcVersionForm
              code={card.code}
              current={versions?.find((v) => v.isCurrent) ?? null}
              onSubmitted={() => setReloadKey((n) => n + 1)}
            />
          </section>
          <section>
            <h2 className="sec">版本历史</h2>
            {versions === null ? (
              <p className="sub-hint">读取中……</p>
            ) : (
              <AtcVersionHistory versions={versions} />
            )}
          </section>
        </div>
      )}
    </article>
  );
}
